import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { Espacio } from '../../../models/sala.model';
import { UiStateComponent } from '../../ui/ui-state.component';
import { UiSkeletonComponent } from '../../ui/ui-skeleton.component';
import { formatMoney, peopleLabel, spaceImage } from './dashboard-view.util';

interface SpaceStatus { label: string; tone: 'green' | 'blue' | 'orange' | 'gray'; }

export type SpaceMenuAction = 'details' | 'edit' | 'reserve';

/**
 * Galería de espacios del negocio con su estado operativo real (activo,
 * en mantenimiento o con reservas hoy) y accesos a las acciones habituales.
 */
@Component({
  selector: 'app-dashboard-spaces',
  standalone: true,
  imports: [CommonModule, UiStateComponent, UiSkeletonComponent],
  template: `
    <section class="spaces" aria-labelledby="spaces-title">
      <header>
        <div>
          <div class="heading-line"><h3 id="spaces-title">Tus espacios</h3><span class="count">{{ total || spaces.length }}</span></div>
          <p>Gestiona y revisa rápidamente el estado de cada espacio.</p>
        </div>
        <button type="button" class="link" (click)="seeAll.emit()">Ver todos <i class="bi bi-arrow-right" aria-hidden="true"></i></button>
      </header>

      <app-ui-skeleton *ngIf="loading && !spaces.length" variant="cards" [count]="3"></app-ui-skeleton>

      <app-ui-state
        *ngIf="!loading && error"
        tone="error"
        icon="bi-wifi-off"
        title="No pudimos cargar tus espacios"
        message="Revisa tu conexión e inténtalo de nuevo."
        actionLabel="Reintentar"
        (action)="retry.emit()"
      ></app-ui-state>

      <app-ui-state
        *ngIf="!loading && !error && !spaces.length"
        tone="empty"
        icon="bi-door-open"
        kubo="explicando"
        title="Aún no tienes espacios"
        message="Publica tu primer espacio y comienza a recibir reservas."
        [actionLabel]="canEdit ? 'Publicar ahora' : ''"
        (action)="seeAll.emit()"
      ></app-ui-state>

      <div class="grid" *ngIf="!error && spaces.length" [class.single]="visible.length === 1" [class.double]="visible.length === 2">
        <article class="card" *ngFor="let space of visible; let i = index">
          <div class="photo">
            <img *ngIf="image(space, i) as imageUrl" [src]="imageUrl" [alt]="space.title" loading="lazy" (error)="onImageError(i)" />
            <div class="photo-empty" *ngIf="!image(space, i)" role="img" [attr.aria-label]="space.title + ' no tiene fotografia'">
              <span class="empty-symbol" aria-hidden="true"><i class="bi bi-building"></i></span>
              <small>Espacio sin fotografía</small>
            </div>
            <span class="badge" [ngClass]="'badge-' + status(space).tone">
              <i class="bi bi-circle-fill" aria-hidden="true"></i>{{ status(space).label }}
            </span>
          </div>
          <div class="info">
            <div class="space-name">
              <strong>{{ space.title }}</strong>
              <small *ngIf="location(space)"><i class="bi bi-geo-alt" aria-hidden="true"></i>{{ location(space) }}</small>
            </div>
            <span class="menu-wrap">
              <button type="button" class="kebab" aria-haspopup="menu" [attr.aria-expanded]="openMenu === i" [attr.aria-label]="'Opciones de ' + space.title" (click)="toggleMenu(i, $event)">
                <i class="bi bi-three-dots-vertical" aria-hidden="true"></i>
              </button>
              <span class="menu" role="menu" *ngIf="openMenu === i">
                <button type="button" role="menuitem" (click)="pick('details', space)"><i class="bi bi-eye" aria-hidden="true"></i> Ver detalles</button>
                <button type="button" role="menuitem" *ngIf="canEdit" (click)="pick('edit', space)"><i class="bi bi-pencil" aria-hidden="true"></i> Editar espacio</button>
                <button type="button" role="menuitem" *ngIf="canCreateReservation" (click)="pick('reserve', space)"><i class="bi bi-calendar-plus" aria-hidden="true"></i> Nueva reserva</button>
              </span>
            </span>
          </div>
          <div class="meta">
            <div class="meta-item"><i class="bi bi-people" aria-hidden="true"></i><span><small>Capacidad</small><strong>{{ people(space) }}</strong></span></div>
            <div class="meta-item"><i class="bi bi-cash-stack" aria-hidden="true"></i><span><small>Tarifa</small><strong>{{ price(space) }}</strong></span></div>
          </div>
          <footer>
            <button type="button" class="ghost" (click)="details.emit(space)"><i class="bi bi-eye" aria-hidden="true"></i>Ver detalles</button>
            <button type="button" class="link" *ngIf="canCreateReservation" (click)="newReservation.emit(space)"><i class="bi bi-calendar-plus" aria-hidden="true"></i>Nueva reserva</button>
          </footer>
        </article>
      </div>

      <p class="more" *ngIf="total > visible.length">Mostrando {{ visible.length }} de {{ total }} espacios · <button type="button" class="link" (click)="seeAll.emit()">ver todos</button></p>
    </section>
  `,
  styles: [`
    :host { display: block; min-width: 0; height: auto; container-type: inline-size; }
    .spaces { box-sizing: border-box; min-width: 0; padding: 1rem; overflow: hidden; border: 1px solid var(--ts-card-border, var(--ts-border)); border-radius: var(--ts-radius-lg); background: var(--ts-surface); box-shadow: var(--ts-card-shadow, var(--ts-shadow-sm)); }
    header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: .7rem; }
    header h3 { margin: 0 0 .16rem; color: var(--ts-text); font-family: "Plus Jakarta Sans", Inter, sans-serif; font-size: 1.02rem; letter-spacing: -.02em; }
    .heading-line { display: flex; align-items: center; gap: .48rem; }
    .count { display: grid; place-items: center; min-width: 22px; height: 22px; padding: 0 .38rem; color: var(--ts-text-blue-300-4); border: 1px solid color-mix(in srgb, var(--ts-primary) 24%, var(--ts-border)); border-radius: 999px; background: color-mix(in srgb, var(--ts-primary) 9%, var(--ts-surface)); font-size: .61rem; font-weight: 850; }
    header p { margin: 0; color: var(--ts-text-muted); font-size: .72rem; }
    .link { display: inline-flex; align-items: center; gap: .3rem; color: var(--ts-text-blue-300-4); border: 0; background: transparent; font: inherit; font-size: .7rem; font-weight: 750; cursor: pointer; }
    .link:hover { text-decoration: underline; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .8rem; align-items: stretch; min-width: 0; }
    .grid.single { grid-template-columns: minmax(0, min(360px, 100%)); }
    .grid.double { grid-template-columns: repeat(2, minmax(0, 360px)); }
    .card { position: relative; z-index: 0; display: flex; min-width: 0; overflow: visible; flex-direction: column; border: 1px solid var(--ts-card-border, var(--ts-border)); border-radius: 14px; background: var(--ts-surface); box-shadow: 0 3px 12px rgba(16,35,62,.08); transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease; }
    .card:hover { z-index: 2; transform: translateY(-2px); border-color: var(--ts-primary); box-shadow: 0 10px 24px rgba(16,35,62,.13); }
    .card:has(.menu) { z-index: 20; border-color: var(--ts-primary); box-shadow: 0 14px 34px rgba(16,35,62,.2); }
    .photo { position: relative; height: 138px; flex: 0 0 138px; overflow: hidden; border-radius: 13px 13px 0 0; background: var(--ts-surface-2); }
    .photo img { display: block; width: 100%; height: 100%; object-fit: cover; }
    .photo-empty { display: grid; place-content: center; justify-items: center; gap: .45rem; width: 100%; height: 100%; color: var(--ts-text-muted); background: linear-gradient(145deg, color-mix(in srgb, var(--ts-primary) 8%, var(--ts-surface)), var(--ts-surface-2)); }
    .empty-symbol { position: relative; display: grid; place-items: center; width: 52px; height: 52px; color: var(--ts-text-blue-300-4); border: 1px solid color-mix(in srgb, var(--ts-primary) 22%, var(--ts-border)); border-radius: 16px; background: color-mix(in srgb, var(--ts-primary) 10%, var(--ts-surface)); font-size: 1.25rem; }
    .photo-empty small { font-size: .61rem; font-weight: 700; }
    .badge { position: absolute; top: .48rem; right: .48rem; display: inline-flex; align-items: center; gap: .3rem; max-width: calc(100% - .96rem); padding: .24rem .48rem; border-radius: 999px; font-size: .59rem; font-weight: 800; white-space: nowrap; box-shadow: 0 3px 10px rgba(0,0,0,.15); }
    /* Sin backdrop-filter: con fondo casi opaco no se notaba, y ponía cada
       etiqueta en su propia capa gráfica -- al hacer scroll se veía flotando
       sobre la tarjeta. */
    .badge i { font-size: .45rem; }
    .badge-green { color: var(--ts-status-success-text, #bbf7d0); border: 1px solid var(--ts-status-success-border, #22c55e); background: var(--ts-status-success-bg, rgba(21,128,61,.86)); }
    .badge-blue { color: var(--ts-sem-primary-deep, #1d4ed8); border: 1px solid var(--ts-sem-primary-solid, #2563eb); background: var(--ts-sem-primary-tint, #dbe7ff); }
    .badge-orange { color: var(--ts-status-warning-text, #fde68a); border: 1px solid var(--ts-status-warning-border, #f59e0b); background: var(--ts-status-warning-bg, rgba(146,64,14,.88)); }
    .badge-gray { color: var(--ts-text); border: 1px solid var(--ts-border-strong); background: var(--ts-surface-inset-strong); }
    .info { display: flex; align-items: flex-start; justify-content: space-between; gap: .6rem; min-width: 0; padding: .75rem .75rem .55rem; border-bottom: 1px solid var(--ts-border-faint-3); background: linear-gradient(90deg, color-mix(in srgb, var(--ts-primary) 6%, var(--ts-surface)), var(--ts-surface)); }
    .space-name { display: grid; gap: .24rem; min-width: 0; }
    .space-name > strong { display: -webkit-box; min-width: 0; overflow: hidden; color: var(--ts-text); font-size: .78rem; line-height: 1.35; overflow-wrap: anywhere; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
    .space-name > small { display: flex; align-items: center; gap: .25rem; min-width: 0; overflow: hidden; color: var(--ts-text-muted); font-size: .59rem; text-overflow: ellipsis; white-space: nowrap; }
    .menu-wrap { position: relative; flex: 0 0 auto; }
    .kebab { display: grid; place-items: center; width: 28px; height: 28px; color: var(--ts-text-muted); border: 1px solid var(--ts-border-faint-3); border-radius: 8px; background: var(--ts-surface); cursor: pointer; }
    .kebab:hover { color: var(--ts-text); border-color: var(--ts-border); background: var(--ts-surface-hover); }
    .menu { position: absolute; z-index: 30; top: calc(100% + .3rem); right: 0; display: grid; min-width: 190px; padding: .35rem; border: 1px solid var(--ts-border-strong); border-radius: 11px; background: var(--ts-bg-elevated); box-shadow: var(--ts-shadow-lg); animation: menu-in .14s ease-out both; }
    @keyframes menu-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
    .menu button { display: flex; align-items: center; gap: .5rem; padding: .5rem .6rem; text-align: left; color: var(--ts-text); border: 0; border-radius: 8px; background: transparent; font: inherit; font-size: .72rem; cursor: pointer; }
    .menu button:hover { background: var(--ts-surface-hover); }
    .meta { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: .42rem; min-width: 0; padding: .65rem .75rem; }
    .meta-item { display: grid; grid-template-columns: 24px minmax(0,1fr); align-items: center; gap: .38rem; min-width: 0; padding: .42rem .48rem; border-radius: 9px; background: var(--ts-surface-soft); }
    .meta-item > i { display: grid; place-items: center; width: 24px; height: 24px; color: var(--ts-text-blue-300-4); border-radius: 7px; background: color-mix(in srgb, var(--ts-primary) 10%, var(--ts-surface)); font-size: .7rem; }
    .meta-item > span { display: grid; min-width: 0; }
    .meta-item small { color: var(--ts-text-muted); font-size: .52rem; line-height: 1.2; }
    .meta-item strong { overflow: hidden; color: var(--ts-text); font-size: .61rem; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
    footer { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); align-items: center; gap: .45rem; margin-top: auto; padding: 0 .75rem .75rem; }
    footer .ghost, footer .link { box-sizing: border-box; width: 100%; min-width: 0; min-height: 36px; justify-content: center; gap: .35rem; padding: 0 .42rem; overflow: hidden; border-radius: 9px; font: inherit; font-size: .64rem; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
    footer .ghost { color: var(--ts-text); border: 1px solid var(--ts-border); background: var(--ts-surface); }
    footer .link { border: 1px solid color-mix(in srgb, var(--ts-primary) 35%, transparent); background: color-mix(in srgb, var(--ts-primary) 8%, transparent); text-decoration: none; }
    footer .ghost:hover { border-color: var(--ts-border-blue-400-a40); background: var(--ts-surface-hover); }
    .more { margin: .65rem 0 0; color: var(--ts-text-muted); font-size: .64rem; text-align: center; }
    @container (max-width: 780px) { .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @container (max-width: 540px) { .grid { grid-template-columns: minmax(0,1fr); } }
    @media (max-width: 900px) {
      .grid { grid-auto-flow: column; grid-auto-columns: minmax(250px, 86%); grid-template-columns: none; overflow-x: auto; padding: .1rem 0 .5rem; scroll-snap-type: x mandatory; scrollbar-width: thin; -webkit-overflow-scrolling: touch; }
      .grid.single, .grid.double { grid-template-columns: none; }
      .card { scroll-snap-align: start; }
    }
    @media (max-width: 520px) {
      .spaces { padding: .85rem; }
      header { align-items: flex-start; }
      header > div { min-width:0; }
      header .link { flex:0 0 auto;white-space:normal;text-align:right; }
      header p { max-width: 34ch; }
      .grid { grid-auto-columns: minmax(238px, 92%); }
      .card { width:100%;max-width:100%; }
      .photo { height:126px;flex-basis:126px; }
      .menu { position:fixed;top:auto;right:12px;bottom:12px;left:12px;min-width:0; }
    }
  `],
})
export class DashboardSpacesComponent implements OnChanges {
  @Input() spaces: Espacio[] = [];
  @Input() total = 0;
  @Input() loading = false;
  @Input() error = false;
  @Input() limit = 3;
  @Input() busySpaceIds: Set<number> = new Set<number>();
  @Input() canCreateReservation = true;
  @Input() canEdit = true;

  @Output() details = new EventEmitter<Espacio>();
  @Output() newReservation = new EventEmitter<Espacio>();
  @Output() seeAll = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();
  @Output() menu = new EventEmitter<{ action: SpaceMenuAction; space: Espacio }>();

  openMenu: number | null = null;
  private failedImages = new Set<number>();

  /** Lista cacheada para no romper *ngFor con un arreglo nuevo por pasada. */
  private visibleCache: Espacio[] = [];

  ngOnChanges(): void {
    this.failedImages.clear();
    this.visibleCache = this.buildVisible();
  }

  get visible(): Espacio[] {
    return this.visibleCache;
  }

  private buildVisible(): Espacio[] {
    const active = this.spaces.filter(s => s.is_active);
    const source = active.length ? active : this.spaces;
    return source.slice(0, this.limit);
  }

  status(space: Espacio): SpaceStatus {
    if (!space.is_active) return { label: 'Inactivo', tone: 'gray' };
    if (space.status === 'under_maintenance') return { label: 'En mantenimiento', tone: 'orange' };
    if (this.busySpaceIds.has(space.space_id)) return { label: 'Con reservas hoy', tone: 'blue' };
    return { label: 'Disponible', tone: 'green' };
  }

  people(space: Espacio): string { return space.capacity ? peopleLabel(space.capacity) : 'Sin aforo'; }

  location(space: Espacio): string {
    return [space.city, space.country].filter(Boolean).join(', ');
  }

  price(space: Espacio): string {
    const amount = Number(space.price_per_hour);
    return Number.isFinite(amount) && amount > 0 ? `${formatMoney(amount)} / hora` : 'Sin tarifa';
  }

  image(space: Espacio, index: number): string | null {
    return this.failedImages.has(space.space_id || index) ? null : spaceImage(space);
  }

  onImageError(index: number): void {
    const space = this.visible[index];
    this.failedImages.add(space?.space_id || index);
  }

  toggleMenu(index: number, event: Event): void {
    event.stopPropagation();
    this.openMenu = this.openMenu === index ? null : index;
  }

  pick(action: SpaceMenuAction, space: Espacio): void {
    this.openMenu = null;
    this.menu.emit({ action, space });
  }

  @HostListener('document:click') closeMenu(): void {
    if (this.openMenu !== null) this.openMenu = null;
  }
}
